/*
<summary>
  This functional component "LoginForm" provides form to login into application on the basis of the credentials provided.
  Developer:Aashish Singh, Created Date:28-Aug-2022, Last Updated By: Mohammad Saquib Khan, Updated Date: 10-Feb-2023
</summary>
<returns>Returns JSX</returns>
*/

import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import ILogin from '../../../models/forms/ILogin';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormCheckBox from '../../../shared-components/FormikFormHandlers/FormikFormCheckBox';
import { LoginValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import LoaderButton from '../../../shared-components/button/loader-button';
import Label from '../../../shared-components/label/label';
import { useStore } from '../../../contexts/store-provider';
import { useHistory } from 'react-router-dom';
import mobileImageLogin from '../../../assets/Images/loginKMS.jpg';
import { EmailIcon, PasswordIcon } from '../../../shared-components/svgs/svgs';

interface IProps {
  loginHandler: (data: ILogin) => void;
  initialValues: ILogin;
  isLoading: boolean;
}

let LoginForm = (props: IProps) => {
  const history = useHistory();
  const { preferencesStore } = useStore();
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<{
    language: string;
    isLanguageChanged: boolean;
  }>({ language: preferencesStore.language, isLanguageChanged: false });

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "selectedLanguage.isLanguageChanged" of the useEffect changed.
   */
  useEffect(() => {
    if (selectedLanguage.isLanguageChanged) {
      preferencesStore.changeLanguage(selectedLanguage.language);
      setSelectedLanguage({ ...selectedLanguage, isLanguageChanged: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage.isLanguageChanged]);

  return (
    <>
      <Formik
        initialValues={props.initialValues}
        validationSchema={LoginValidateSchema}
        validateOnBlur={false}
        onSubmit={async (values) => {
          props.loginHandler(values);
        }}
      >
        <Form>
          <div className="form-login display d-flex justify-content-between align-items-center">
            <div className="loginBox w-100">
              <div className="form-group mb-2">
                <span className="loginSvg">
                  {' '}
                  <EmailIcon />
                </span>
                <Field
                  data-testid="testUsername"
                  className="form-control"
                  name="UserId"
                  type="text"
                  invalidcharacter={[' ']}
                  placeholder="user_label_login_id"
                  validcharacter={[
                    '-',
                    '_',
                    '[',
                    ']',
                    '(',
                    ')',
                    ':',
                    '#',
                    '@',
                    '.',
                  ]}
                  as={FormikFormInput}
                />
              </div>
              <div className="showHideOuterDiv form-group my-3">
                <span className="loginSvg">
                  {' '}
                  <PasswordIcon />
                </span>
                <Field
                  data-testid="testPassword"
                  className="form-control password_field"
                  name="Password"
                  type={showHidePassword ? 'text' : 'password'}
                  invalidcharacter={[' ']}
                  validcharacter={[
                    '~',
                    '`',
                    '!',
                    '@',
                    '#',
                    '$',
                    '%',
                    '^',
                    '&',
                    '*',
                    '(',
                    ')',
                    '_',
                    '-',
                    '+',
                    '=',
                    '{',
                    '[',
                    '}',
                    ']',
                    '|',
                    '\\',
                    ':',
                    ';',
                    "'",
                    '"',
                    '<',
                    ',',
                    '>',
                    '.',
                    '?',
                    '/',
                  ]}
                  placeholder="user_label_password"
                  as={FormikFormInput}
                />

                {showHidePassword ? (
                  <svg
                    type="button"
                    onClick={() => {
                      setShowHidePassword(!showHidePassword);
                    }}
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    stroke="#000"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="showHidePass css-i6dzq1"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                    ></circle>
                  </svg>
                ) : (
                  <svg
                    type="button"
                    onClick={() => {
                      setShowHidePassword(!showHidePassword);
                    }}
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    stroke="#000"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="showHidePass css-i6dzq1"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line
                      x1="1"
                      y1="1"
                      x2="23"
                      y2="23"
                    ></line>
                  </svg>
                )}
              </div>

              {/* <a
                className="forgetPas"
                href=""
              >
                <FormattedMessage id="forgot_password" />?
              </a> */}
              <LoaderButton
                type="submit"
                className="btn btn-blue btn btn-primary login-btn w-100 mt-3"
                isLoading={props.isLoading}
                text="login"
              />
              {/* <p className="register_message">
                <FormattedMessage id="dont_have_account" />?{' '}
                <a href="text-secondary">
                  <FormattedMessage id="register_here" />
                </a>
              </p> */}
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
};

export default LoginForm;
